import React, { useState } from "react";
import { AiOutlineClose, AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useForm } from "react-hook-form";
import useOutsideClick from "hooks/click-outside";
import { warningToaster } from "utils/helpers/common/alert-service";
import useAttendee from "containers/attendee/useHooks";
import useUtils from "hooks/useUtils";
import { ChangePasswordDTO } from "utils/helpers/models/shared/change-password.dto";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const PasswordPopup: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useOutsideClick(onClose);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRetypePassword, setShowRetypePassword] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordDTO>();
  const { changeMinorPassword } = useAttendee();
  const { getQueryParams } = useUtils();
  const params = getQueryParams();

  const onSubmit = (data: ChangePasswordDTO) => {
    if (data.newPassword !== data.confirmPassword) {
      warningToaster("Passwords do not match!");
    } else {
      changeMinorPassword(params?.id, data, onClose);
    }
  };

  if (!isOpen) return null;

  return (
    <div
    style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
      className={`fixed  inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[375px] transform transition-transform duration-300 ${isOpen ? "scale-100" : "scale-95"
          }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
        >
          <AiOutlineClose size={20} />
        </button>

        <h2 className="text-xl text-center text-primaryBlueText font-bold mb-4">
          Reset Password
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className="text-label text-base font-semibold">
                Password
              </label>
              <div
                className="cursor-pointer flex items-center text-label"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <>
                    <AiFillEyeInvisible size={18} className="mr-1" />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <AiFillEye size={18} className="mr-1" />
                    <span>Show</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                {...register("newPassword", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long",
                  },
                  validate: (value) => {
                    if (/\s/.test(value)) {
                      return "Password cannot contain spaces";
                    }
                    if (!/(?=.*[a-z])/.test(value)) {
                      return "Password must include at least one lowercase letter";
                    }
                    if (!/(?=.*[A-Z])/.test(value)) {
                      return "Password must include at least one capital letter";
                    }
                    if (!/(?=.*\d)/.test(value)) {
                      return "Password must include at least one number";
                    }
                    if (!/(?=.*[^\w\s])/.test(value)) {
                      return "Password must include at least one special character";
                    }
                    return true;
                  },
                })}
                className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              />
            </div>
            {errors.newPassword && (
              <span className="text-red-500 text-sm">
                {errors.newPassword.message}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className="text-label text-base font-semibold">
                Confirm Password
              </label>
              <div
                className="cursor-pointer flex items-center text-label"
                onClick={() => setShowRetypePassword(!showRetypePassword)}
              >
                {showRetypePassword ? (
                  <>
                    <AiFillEyeInvisible size={18} className="mr-1" />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <AiFillEye size={18} className="mr-1" />
                    <span>Show</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <input
                type={showRetypePassword ? "text" : "password"}
                placeholder="Confirm your password"
                {...register("confirmPassword", {
                  required: "Confirm password is required",
                  validate: (value) => {
                    if (/\s/.test(value)) {
                      return "Password cannot contain spaces";
                    }
                    if (value !== watch("newPassword")) {
                      return "Passwords do not match";
                    }
                    return true;
                  },
                })}
                className="w-full h-[51px] px-3 rounded-lg border border-gray-300 outline-none"
              />
            </div>
            {errors.confirmPassword && (
              <span className="text-red-500 text-sm">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-primaryBlueBg text-white py-3 rounded-lg hover:bg-opacity-90 transition"
          >
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordPopup;
